import { Task } from './task';
import { Project } from './project';

// ============================================
// GANTT CHART TYPES
// ============================================

export interface GanttTask {
  id: string;
  name: string;
  start: Date;
  end: Date;
  progress: number; // 0-100
  dependencies?: string[]; // Task IDs that this task depends on
  assignedTo?: string;
  assignedToName?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'todo' | 'in_progress' | 'completed' | 'cancelled';
  type: 'task' | 'milestone' | 'project';
  
  // Original task reference
  taskId?: string;
  projectId?: string;
  
  // Display properties
  color?: string;
  isCollapsed?: boolean;
  level?: number; // Hierarchy level (0 = project, 1 = task, 2 = subtask)
  
  // Calculated properties
  duration?: number; // Days
  isOverdue?: boolean;
  isCriticalPath?: boolean;
}

export interface GanttMilestone {
  id: string;
  name: string;
  date: Date;
  projectId: string;
  description?: string;
  isCompleted: boolean;
}

export interface GanttDependency {
  id: string;
  fromTaskId: string;
  toTaskId: string;
  type: 'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish';
  lag?: number; // Days
}

export interface GanttViewSettings {
  // Time scale
  timeScale: 'day' | 'week' | 'month' | 'quarter';
  
  // Display options
  showWeekends: boolean;
  showToday: boolean;
  showDependencies: boolean;
  showProgress: boolean;
  showCriticalPath: boolean;
  
  // Zoom level
  zoomLevel: number; // 1-5 (1 = most zoomed out, 5 = most zoomed in)
  
  // Date range
  startDate?: Date;
  endDate?: Date;
  
  // Grouping
  groupBy?: 'none' | 'status' | 'priority' | 'assignee';
}

export interface GanttChartData {
  tasks: GanttTask[];
  milestones: GanttMilestone[];
  dependencies: GanttDependency[];
  project: Project;
  settings: GanttViewSettings;
}

// ============================================
// GANTT HELPER TYPES
// ============================================

export interface TimelineColumn {
  date: Date;
  label: string;
  isWeekend: boolean;
  isToday: boolean;
  type: 'day' | 'week' | 'month';
}

export interface TaskBar {
  task: GanttTask;
  x: number; // Position in pixels
  width: number; // Width in pixels
  y: number; // Row position
}

export interface DependencyLine {
  from: { x: number; y: number };
  to: { x: number; y: number };
  type: GanttDependency['type'];
}

// ============================================
// GANTT CONSTANTS
// ============================================

export const GANTT_ROW_HEIGHT = 40;
export const GANTT_HEADER_HEIGHT = 60;
export const GANTT_SIDEBAR_WIDTH = 300;
export const GANTT_DAY_WIDTH = 40;
export const GANTT_WEEK_WIDTH = 80;
export const GANTT_MONTH_WIDTH = 120;

export const GANTT_COLORS = {
  todo: '#6B7280',
  in_progress: '#3B82F6',
  completed: '#10B981',
  cancelled: '#EF4444',
  
  priority: {
    low: '#22C55E',
    medium: '#EAB308',
    high: '#F97316',
    urgent: '#EF4444'
  },
  
  milestone: '#9333EA',
  dependency: '#94A3B8',
  criticalPath: '#DC2626',
  today: '#9333EA',
  weekend: '#F3F4F6'
};

// ============================================
// GANTT UTILITY FUNCTIONS
// ============================================

export const convertTaskToGanttTask = (task: Task): GanttTask | null => {
  // Use createdAt as start date
  const start = task.createdAt;
  
  // Use dueDate if available, otherwise use createdAt + 7 days as default
  const end = task.dueDate || new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  // Calculate progress based on status
  let progress = 0;
  switch (task.status) {
    case 'completed':
      progress = 100;
      break;
    case 'in_progress':
      progress = 50;
      break;
    case 'cancelled':
      progress = 0;
      break;
    default:
      progress = 0;
  }
  
  return {
    id: task.id,
    name: task.title,
    start,
    end,
    progress,
    assignedTo: task.assignedTo,
    assignedToName: task.assignedToName,
    priority: task.priority,
    status: task.status,
    type: 'task',
    taskId: task.id,
    projectId: task.projectId,
    color: GANTT_COLORS[task.status],
    level: 1
  };
};

export const convertProjectToGanttTask = (project: Project): GanttTask => {
  return {
    id: project.id,
    name: project.name,
    start: project.startDate,
    end: project.endDate,
    progress: project.progress,
    priority: project.priority,
    status: project.status === 'active' ? 'in_progress' : 
            project.status === 'completed' ? 'completed' : 'todo',
    type: 'project',
    projectId: project.id,
    color: '#9333EA',
    level: 0
  };
};
